import { useEffect, useState } from 'react';

import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/styles.css';
import { useNotification } from '../Contexts/NotificationContext';

const ImageItem = ({ slides, slide, handleSelection, selectedItems, showSelectButton }) => {
    const { setNotification } = useNotification();
    const [open, setOpen] = useState(false);
    const [viewed, setViewed] = useState(false);
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

    const isSelected = selectedItems && selectedItems.includes(slide.id);

    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleOpenLightbox = (index) => {
        setCurrentSlideIndex(index);
        setOpen(true);
    };

    const handleDownload = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch(
                `${baseUrl}/painel/materiais/${open ? slides[currentSlideIndex].produto_slug : slide.produto_slug}/imagens/${open ? slides[currentSlideIndex].id : slide.id}/download`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Erro ao baixar arquivo: ${response.statusText}`);
            }

            const blob = await response.blob();

            let fileName = open ? slides[currentSlideIndex].nome : slide.nome;
            const contentDisposition = response.headers.get('Content-Disposition');

            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match && match[1]) {
                    fileName = match[1];
                }
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        }
    };

    const handleShare = async () => {
        const shareUrl = `${window.location.origin}/arquivos/${open ? slides[currentSlideIndex].produto_slug : slide.produto_slug}/imagens/${open ? slides[currentSlideIndex].id : slide.id}`;
        const shareText = `Veja essa imagem da Rometal: ${open ? slides[currentSlideIndex].nome : slide.nome}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Compartilhar',
                    text: shareText,
                    url: shareUrl,
                });
            } catch (error) {
                setNotification({
                    type: 'error',
                    message: 'Erro ao compartilhar',
                    show: true,
                });
            }
        } else {
            setNotification({
                type: 'alert',
                message: 'O compartilhamento não é suportado neste navegador.',
                show: true,
            });
        }
    };

    useEffect(() => {
        if (open && !viewed) {
            const updateViews = async () => {
                try {
                    const response = await fetch(`${baseUrl}/painel/materiais/${slide.produto_slug}/visualizar/imagem/${slide.id}`, {
                        method: 'PUT',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    });

                    if (!response.ok) {
                        throw new Error('Erro ao atualizar progresso.');
                    }

                    setViewed(true);
                } catch (error) {
                    setNotification({
                        type: 'error',
                        message: error.message,
                        show: true,
                    });
                }
            };

            updateViews();
        }
    }, [open]);

    return (
        <>
            <div className="group block relative rounded-xl border-2 border-secondary z-[1]">
                <h5
                    className={`bg-secondary rounded-t px-1.5 pb-1 pt-0.5 text-xs text-white font-light line-clamp-1${showSelectButton ? ' select-none' : ''}`}
                >
                    {slide.nome}
                </h5>
                <div
                    className="relative w-full h-0 pb-[100%] bg-white cursor-pointer overflow-hidden"
                    onClick={() => handleOpenLightbox(slides.findIndex((s) => s.id === slide.id))}
                >
                    <img
                        src={slide.preview}
                        className={`absolute w-full h-full object-cover transition-transform duration-200 group-hover:scale-105${showSelectButton ? ' select-none' : ''}`}
                        loading="lazy"
                        alt="imagem preview"
                    />
                </div>
                <button
                    className={`w-full flex items-center justify-center gap-1.5 md:gap-2 py-1 text-xs text-white font-semibold md:tracking-widest uppercase transition-all hover:opacity-75${showSelectButton ? ' select-none' : ''}`}
                    onClick={handleDownload}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="13.5" height="10.8" viewBox="0 0 18 14.4" className="fill-white">
                        <g transform="translate(-1.8 -3.6)">
                            <path d="M10.8,14.4l3.6-4.5H11.7V3.6H9.9V9.9H7.2Z" />
                            <path d="M18,16.2H3.6V9.9H1.8v6.3A1.8,1.8,0,0,0,3.6,18H18a1.8,1.8,0,0,0,1.8-1.8V9.9H18Z" />
                        </g>
                    </svg>
                    Download
                </button>

                {handleSelection && showSelectButton && (
                    <div className="absolute inset-0">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelection(slide.id)}
                            className="appearance-none w-full h-full cursor-pointer"
                        />
                    </div>
                )}

                {handleSelection && showSelectButton && (
                    <div
                        className={`absolute -inset-1.5 md:-inset-4 bg-secondary z-[-1] rounded-2xl opacity-25 transition-all ${isSelected ? 'scale-100' : 'scale-0'}`}
                    />
                )}
            </div>

            {open && (
                <Lightbox
                    plugins={[Captions]}
                    open={open}
                    close={() => setOpen(false)}
                    index={currentSlideIndex}
                    slides={slides.map((slide) => ({
                        src: slide.preview,
                        id: slide.id,
                        description: slide.nome,
                    }))}
                    on={{
                        view: ({ index }) => {
                            setCurrentSlideIndex(index);
                        },
                    }}
                    toolbar={{
                        buttons: [
                            <button key="share-button" onClick={handleShare} className="px-4 py-2 mt-0.5" title="Compartilhar">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="19.2"
                                    viewBox="0 0 18 14.4"
                                    className="fill-[hsla(0,0%,100%,.8)] hover:fill-white"
                                >
                                    <g transform="translate(-1.8 -3.6)">
                                        <path
                                            d="M10.8,14.4l3.6-4.5H11.7V3.6H9.9V9.9H7.2Z"
                                            style={{ transformBox: 'fill-box', transformOrigin: '50% 50%' }}
                                            transform="matrix(-1, 0, 0, -1, -0.000001, -0.000002)"
                                        />
                                        <path d="M18,16.2H3.6V9.9H1.8v6.3A1.8,1.8,0,0,0,3.6,18H18a1.8,1.8,0,0,0,1.8-1.8V9.9H18Z" />
                                    </g>
                                </svg>
                            </button>,
                            <button key="download-button" onClick={handleDownload} className="px-4 py-2 mt-0.5" title="Baixar">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="19.2"
                                    viewBox="0 0 18 14.4"
                                    className="fill-[hsla(0,0%,100%,.8)] hover:fill-white"
                                >
                                    <g transform="translate(-1.8 -3.6)">
                                        <path d="M10.8,14.4l3.6-4.5H11.7V3.6H9.9V9.9H7.2Z" />
                                        <path d="M18,16.2H3.6V9.9H1.8v6.3A1.8,1.8,0,0,0,3.6,18H18a1.8,1.8,0,0,0,1.8-1.8V9.9H18Z" />
                                    </g>
                                </svg>
                            </button>,
                            'close',
                        ],
                    }}
                />
            )}
        </>
    );
};

export default ImageItem;
